<template>
  <Transition name="help-chat-slide">
    <div v-if="isOpen" class="help-chat">
      <div class="help-chat__panel" role="dialog" aria-label="Chat de ayuda">
        <header class="help-chat__header">
          <div class="help-chat__header-copy">
            <div class="help-chat__title">Ayuda</div>
            <div class="help-chat__subtitle">Estamos para escucharte</div>
          </div>
          <v-btn icon variant="text" size="small" color="white" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <TransitionGroup ref="messagesEl" name="help-msg" tag="div" class="help-chat__messages">
          <div
            v-for="message in messages"
            :key="message.id"
            class="help-chat__bubble"
            :class="`help-chat__bubble--${message.role}`"
          >
            <div class="help-chat__bubble-text">{{ message.text }}</div>

            <div v-if="message.options?.length" class="help-chat__options">
              <v-btn
                v-for="option in message.options"
                :key="option.value"
                size="small"
                rounded="lg"
                class="help-chat__option-btn"
                :variant="option.disabled ? 'outlined' : 'tonal'"
                :color="option.disabled ? 'grey' : 'primary'"
                :disabled="option.disabled || step !== 'menu' || isBotTyping"
                @click="onSelectOption(option.value)"
              >
                {{ option.label }}
              </v-btn>
            </div>

            <div v-if="message.actions?.length" class="help-chat__options">
              <v-btn
                v-for="(action, actionIndex) in message.actions"
                :key="`${message.id}-${action.type}-${actionIndex}`"
                size="small"
                rounded="lg"
                class="help-chat__option-btn"
                variant="tonal"
                color="primary"
                :disabled="isBotTyping || sending"
                @click="onAssistantAction(action)"
              >
                {{ action.label }}
              </v-btn>
            </div>
          </div>

          <div
            v-if="isBotTyping"
            key="typing"
            class="help-chat__bubble help-chat__bubble--bot help-chat__typing"
          >
            <span class="help-chat__typing-dot" />
            <span class="help-chat__typing-dot" />
            <span class="help-chat__typing-dot" />
          </div>
        </TransitionGroup>

        <div v-if="step === 'ask-guest'" class="help-chat__footer-actions help-chat__footer-actions--stack">
          <v-btn color="primary" block rounded="lg" @click="goToRegister">
            Ir al registro
          </v-btn>
          <v-btn variant="text" color="primary" @click="resetConversation">
            Volver al menú
          </v-btn>
        </div>

        <div v-else-if="step === 'contact'" class="help-chat__contact">
          <v-text-field
            v-model="contactName"
            label="Nombre"
            density="compact"
            variant="outlined"
            hide-details="auto"
            class="mb-2"
          />
          <v-text-field
            v-model="contactEmail"
            label="Correo"
            type="email"
            density="compact"
            variant="outlined"
            :error-messages="contactEmailError"
            hide-details="auto"
            class="mb-2"
          />
          <div class="text-caption text-medium-emphasis mb-2">
            Indica tu nombre y correo para poder hacer seguimiento.
          </div>
          <v-btn
            color="primary"
            block
            rounded="lg"
            :disabled="!canContinueContact"
            @click="confirmContact"
          >
            Continuar
          </v-btn>
        </div>

        <form
          v-else-if="step === 'message' || step === 'ask'"
          class="help-chat__composer"
          @submit.prevent="step === 'ask' ? submitAskMessage() : submitFeedback()"
        >
          <div class="help-chat__composer-row">
            <v-textarea
              v-model="draft"
              :placeholder="messagePlaceholder"
              :maxlength="composerMaxLength"
              rows="3"
              auto-grow
              max-rows="6"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :disabled="sending"
            />
            <v-btn
              type="submit"
              color="primary"
              icon
              class="help-chat__send"
              :loading="sending"
              :disabled="!canSubmitComposer"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div
            class="help-chat__char-hint"
            :class="{ 'help-chat__char-hint--short': isComposerTooShort }"
          >
            ({{ draftLength }} / {{ composerMaxLength }}) caracteres. Mínimo
            {{ composerMinLength }}
          </div>
        </form>

        <div v-else-if="step === 'done'" class="help-chat__footer-actions">
          <v-btn variant="text" color="primary" @click="resetConversation">
            Enviar otro mensaje
          </v-btn>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useAuthStore } from '@/stores/auth'
import { useHelpChatStore, type HelpFeedbackType } from '@/stores/helpChat'
import { useRightPanelStore } from '@/stores/rightPanel'
import AuthService from '@/services/auth'
import HelpAssistantService, {
  type HelpAssistantAction,
} from '@/services/helpAssistant'
import { useToast } from '@/composables/useToast'

type ChatRole = 'bot' | 'user'

type ChatOption = {
  label: string
  value: 'bug' | 'suggestion' | 'ask'
  disabled?: boolean
}

type ChatMessage = {
  id: number
  role: ChatRole
  text: string
  options?: ChatOption[]
  actions?: HelpAssistantAction[]
}

type ChatStep = 'menu' | 'contact' | 'message' | 'ask' | 'ask-guest' | 'done'

const MENU_OPTIONS: ChatOption[] = [
  { label: 'Reportar un error', value: 'bug' },
  { label: 'Sugerir una mejora', value: 'suggestion' },
  { label: 'Pedir ayuda', value: 'ask' },
]

const MIN_MESSAGE_LENGTH = 20
const MAX_MESSAGE_LENGTH = 4000
const MIN_ASK_LENGTH = 2
const MAX_ASK_LENGTH = 1000
const EMAIL_REGEX = /.+@.+\..+/
const ALLOWED_NAVIGATE_ROUTE_NAMES = new Set([
  'how-it-works',
  'projects',
  'trash',
  'config',
  'images',
  'progress',
  'writing-sessions',
  'challenges',
  'writing-feedback',
  'sessions',
  'profile',
  'account',
  'login',
  'terms-and-conditions',
  'project',
  'community-activity',
  'community-groups',
  'community-capsule',
])

export default defineComponent({
  name: 'HelpChat',

  setup() {
    const helpChatStore = useHelpChatStore()
    const authStore = useAuthStore()
    const rightPanelStore = useRightPanelStore()
    const route = useRoute()
    const router = useRouter()
    const toast = useToast()
    const { isOpen } = storeToRefs(helpChatStore)
    const { mobile } = useDisplay()

    return {
      helpChatStore,
      authStore,
      rightPanelStore,
      route,
      router,
      toast,
      isOpen,
      mobile,
      MIN_MESSAGE_LENGTH,
      MAX_MESSAGE_LENGTH,
      MIN_ASK_LENGTH,
      MAX_ASK_LENGTH,
    }
  },

  data() {
    return {
      messages: [] as ChatMessage[],
      step: 'menu' as ChatStep,
      selectedType: null as HelpFeedbackType | null,
      contactName: '',
      contactEmail: '',
      draft: '',
      sending: false,
      isBotTyping: false,
      nextMessageId: 1,
      assistantChatId: null as string | null,
    }
  },

  computed: {
    isGuestUser(): boolean {
      return this.authStore.userType === 'guest'
    },

    hasContactInfo(): boolean {
      return Boolean(this.contactName.trim() && this.contactEmail.trim())
    },

    isContactEmailValid(): boolean {
      const email = this.contactEmail.trim()
      return email !== '' && EMAIL_REGEX.test(email)
    },

    contactEmailError(): string | undefined {
      if (!this.contactEmail.trim()) {
        return undefined
      }
      if (this.isContactEmailValid) {
        return undefined
      }
      return 'El correo debe ser válido'
    },

    canContinueContact(): boolean {
      return this.hasContactInfo && this.isContactEmailValid && !this.isBotTyping
    },

    messagePlaceholder(): string {
      if (this.step === 'ask') {
        return '¿Qué quieres hacer en Escribonauta?'
      }
      if (this.selectedType === 'suggestion') {
        return 'Describe tu sugerencia con el mayor detalle posible...'
      }
      return 'Describe el bug con el mayor detalle posible...'
    },

    draftLength(): number {
      return this.draft.length
    },

    composerMinLength(): number {
      return this.step === 'ask' ? MIN_ASK_LENGTH : MIN_MESSAGE_LENGTH
    },

    composerMaxLength(): number {
      return this.step === 'ask' ? MAX_ASK_LENGTH : MAX_MESSAGE_LENGTH
    },

    isComposerTooShort(): boolean {
      return this.draft.trim().length < this.composerMinLength
    },

    canSubmitComposer(): boolean {
      return (
        this.draft.trim().length >= this.composerMinLength &&
        this.draft.length <= this.composerMaxLength &&
        !this.sending &&
        !this.isBotTyping
      )
    },
  },

  watch: {
    async isOpen(open: boolean) {
      if (!open) {
        return
      }

      const suggestionPrefill = this.helpChatStore.consumeSuggestionPrefill()
      this.resetConversation()
      if (suggestionPrefill) {
        await this.startFeedbackFromAssistant('suggestion', suggestionPrefill)
      }
    },
  },

  methods: {
    close() {
      this.helpChatStore.close()
    },

    sleep(ms: number) {
      return new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms)
      })
    },

    resetConversation() {
      this.messages = []
      this.step = 'menu'
      this.selectedType = null
      this.contactName = ''
      this.contactEmail = ''
      this.draft = ''
      this.sending = false
      this.isBotTyping = false
      this.nextMessageId = 1
      this.assistantChatId = null
      this.pushBot('¿En qué podemos ayudarte? Elige una opción:', MENU_OPTIONS)
    },

    pushBot(text: string, options?: ChatOption[], actions?: HelpAssistantAction[]) {
      this.messages.push({
        id: this.nextMessageId,
        role: 'bot',
        text,
        options,
        actions,
      })
      this.nextMessageId += 1
      this.scrollToBottom()
    },

    pushUser(text: string) {
      this.messages.push({
        id: this.nextMessageId,
        role: 'user',
        text,
      })
      this.nextMessageId += 1
      this.scrollToBottom()
    },

    async pushBotAfterDelay(
      text: string,
      options?: ChatOption[],
      delayMs = 380,
      actions?: HelpAssistantAction[],
    ) {
      this.isBotTyping = true
      this.scrollToBottom()
      await this.sleep(delayMs)
      this.isBotTyping = false
      this.pushBot(text, options, actions)
    },

    async scrollToBottom() {
      await nextTick()
      const ref = this.$refs.messagesEl as { $el?: HTMLElement } | HTMLElement | undefined
      const el = ref && '$el' in ref ? ref.$el : (ref as HTMLElement | undefined)
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },

    async onSelectOption(value: ChatOption['value']) {
      if (this.step !== 'menu' || this.isBotTyping) {
        return
      }

      if (value === 'ask') {
        this.pushUser('Pedir ayuda')
        if (this.isGuestUser) {
          await this.pushBotAfterDelay(
            'La ayuda automática es exclusiva para usuarios registrados. Crea una cuenta para usarla.',
          )
          this.step = 'ask-guest'
          return
        }
        await this.pushBotAfterDelay(
          'Esta ayuda automática está en modo de prueba: puede cometer errores y tiene limitaciones.\n\n'
            + 'Pregúntame qué puedes hacer en Escribonauta y cómo hacerlo. Si quieres, también puedo llevarte a la vista correspondiente.',
        )
        this.step = 'ask'
        return
      }

      this.selectedType = value
      const label = value === 'bug' ? 'Reportar un problema' : 'Sugerir una mejora'
      this.pushUser(label)

      if (this.isGuestUser) {
        await this.pushBotAfterDelay(
          'Como estás como invitado, indícame tu nombre y correo para poder hacer seguimiento.',
        )
        this.step = 'contact'
        return
      }

      await this.askForMessage()
    },

    goToRegister() {
      this.helpChatStore.close()
      this.router.push({ name: 'login' })
    },

    async confirmContact() {
      if (!this.canContinueContact) {
        return
      }

      const parts = [
        this.contactName.trim() && `Nombre: ${this.contactName.trim()}`,
        this.contactEmail.trim() && `Correo: ${this.contactEmail.trim()}`,
      ].filter(Boolean)
      this.pushUser(parts.join(' · '))
      await this.askForMessage()
    },

    async askForMessage() {
      const prompt =
        this.selectedType === 'suggestion'
          ? 'Cuéntame tu sugerencia con el mayor detalle posible.'
          : 'Describe el bug con el mayor detalle posible: qué esperabas, qué ocurrió y cómo reproducirlo.'
      await this.pushBotAfterDelay(prompt)
      this.step = 'message'
    },

    buildRouteQuery(): Record<string, unknown> {
      const query: Record<string, unknown> = {}
      Object.entries(this.route.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query[key] = value
        }
      })
      return query
    },

    buildAskContext() {
      const projectId =
        typeof this.route.params.projectId === 'string' ? this.route.params.projectId : undefined
      const chapterId =
        typeof this.route.query.chapterId === 'string' ? this.route.query.chapterId : undefined
      const tab = typeof this.route.query.tab === 'string' ? this.route.query.tab : undefined
      const detail =
        typeof this.route.query.detail === 'string' ? this.route.query.detail : undefined

      return {
        routePath: this.route.fullPath,
        routeName: typeof this.route.name === 'string' ? this.route.name : undefined,
        routeQuery: this.buildRouteQuery(),
        projectId,
        chapterId,
        tab,
        detail,
        display: this.mobile ? ('mobile' as const) : ('desktop' as const),
        locale: 'es',
      }
    },

    async submitAskMessage() {
      if (!this.canSubmitComposer || this.step !== 'ask') {
        return
      }

      const message = this.draft.trim()
      this.pushUser(message)
      this.draft = ''
      this.sending = true
      this.isBotTyping = true
      this.scrollToBottom()

      try {
        const payload = await HelpAssistantService.chat({
          ...(this.assistantChatId ? { assistantChatId: this.assistantChatId } : {}),
          message,
          context: this.buildAskContext(),
        })
        this.assistantChatId = payload.assistantChatId
        this.isBotTyping = false
        this.pushBot(payload.reply, undefined, payload.actions || [])
      } catch {
        this.isBotTyping = false
        await this.pushBotAfterDelay(
          'No pude responder ahora. Inténtalo de nuevo en unos minutos.',
          undefined,
          300,
        )
        this.draft = message
      } finally {
        this.sending = false
      }
    },

    async onAssistantAction(action: HelpAssistantAction) {
      if (action.type === 'navigate') {
        await this.executeNavigateAction(action)
        return
      }
      if (action.type === 'start_suggestion') {
        await this.startFeedbackFromAssistant('suggestion', action.prefillMessage)
        return
      }
      if (action.type === 'start_bug_report') {
        await this.startFeedbackFromAssistant('bug', action.prefillMessage)
      }
    },

    async executeNavigateAction(action: Extract<HelpAssistantAction, { type: 'navigate' }>) {
      if (!ALLOWED_NAVIGATE_ROUTE_NAMES.has(action.routeName)) {
        return
      }

      const query: Record<string, string> = {}
      Object.entries(action.query || {}).forEach(([key, value]) => {
        if (typeof value === 'string' && value) {
          query[key] = value
        }
      })

      if (action.requiresProject) {
        const projectId =
          typeof this.route.params.projectId === 'string' ? this.route.params.projectId : undefined
        if (!projectId) {
          this.pushBot(
            'Para llevarte ahí necesito que abras un proyecto primero. Ve a Mis proyectos y entra a uno.',
            undefined,
            [
              {
                type: 'navigate',
                navigateId: 'app.projects',
                label: 'Ir a Mis proyectos',
                routeName: 'projects',
                requiresProject: false,
              },
            ],
          )
          return
        }

        const chapterId =
          typeof this.route.query.chapterId === 'string' ? this.route.query.chapterId : undefined
        if (chapterId && !query.chapterId) {
          query.chapterId = chapterId
        }

        await this.router.push({
          name: 'project',
          params: { projectId },
          query,
        })
        if (action.openRightPanel) {
          this.rightPanelStore.showPanel()
        }
        this.pushUser(action.label)
        await this.pushBotAfterDelay('Listo, te llevé a esa vista.')
        return
      }

      await this.router.push({
        name: action.routeName,
        query: Object.keys(query).length ? query : undefined,
      })
      this.pushUser(action.label)
      await this.pushBotAfterDelay('Listo, te llevé a esa vista.')
    },

    async startFeedbackFromAssistant(type: HelpFeedbackType, prefillMessage: string) {
      this.selectedType = type
      this.assistantChatId = null
      this.draft = prefillMessage
      this.pushUser(type === 'bug' ? 'Reportar un error' : 'Enviar sugerencia de mejora')
      await this.pushBotAfterDelay(
        type === 'bug'
          ? 'Revisa o completa el reporte y envíalo cuando esté listo.'
          : 'Revisa o completa la sugerencia y envíala cuando esté lista.',
      )
      this.step = 'message'
    },

    async submitFeedback() {
      if (!this.canSubmitComposer || !this.selectedType || this.step !== 'message') {
        return
      }

      const message = this.draft.trim()
      this.pushUser(message)
      this.draft = ''
      this.sending = true

      try {
        await AuthService.feedback({
          type: this.selectedType,
          message,
          routePath: this.route.fullPath,
          routeName: typeof this.route.name === 'string' ? this.route.name : undefined,
          routeQuery: this.buildRouteQuery(),
          ...(this.isGuestUser
            ? {
                contactName: this.contactName.trim(),
                contactEmail: this.contactEmail.trim(),
              }
            : {}),
        })

        await this.pushBotAfterDelay(
          '¡Gracias! Recibimos tu mensaje y lo revisaremos lo antes posible.',
          undefined,
          450,
        )
        this.step = 'done'
        this.toast.triggerToast({
          message: 'Mensaje enviado correctamente',
          color: 'success',
        })
      } catch {
        await this.pushBotAfterDelay(
          'No pudimos enviar tu mensaje. Inténtalo de nuevo en unos minutos.',
          undefined,
          450,
        )
        this.step = 'message'
        this.draft = message
      } finally {
        this.sending = false
      }
    },
  },
})
</script>

<style scoped>
.help-chat {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2200;
  width: min(400px, calc(100vw - 32px));
  pointer-events: none;
}

.help-chat::before {
  content: '';
  position: absolute;
  inset: -48px -36px -32px;
  z-index: 0;
  border-radius: 48px;
  background: radial-gradient(
    ellipse 90% 80% at 50% 55%,
    rgba(var(--v-theme-primary), 0.4) 0%,
    rgba(var(--v-theme-primary), 0.18) 45%,
    transparent 74%
  );
  filter: blur(26px);
  opacity: 0.95;
  pointer-events: none;
}

.help-chat__panel {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  height: min(580px, calc(100vh - 56px));
  background: rgba(var(--v-theme-surface), 0.92);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 22px;
  box-shadow:
    0 0 0 1px rgba(var(--v-theme-primary), 0.08),
    0 0 32px rgba(var(--v-theme-primary), 0.28),
    0 0 64px rgba(var(--v-theme-primary), 0.14),
    0 2px 8px rgba(0, 0, 0, 0.06),
    0 16px 40px rgba(0, 0, 0, 0.14),
    0 32px 64px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.help-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-primary)) 0%,
    rgba(var(--v-theme-primary), 0.88) 100%
  );
  color: white;
}

.help-chat__header-copy {
  min-width: 0;
}

.help-chat__title {
  font-size: 1.05rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.help-chat__subtitle {
  margin-top: 2px;
  font-size: 0.75rem;
  opacity: 0.88;
}

.help-chat__messages {
  position: relative;
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background:
    radial-gradient(circle at top right, rgba(var(--v-theme-primary), 0.06), transparent 40%),
    rgba(var(--v-theme-on-surface), 0.025);
}

.help-chat__bubble {
  max-width: 88%;
  padding: 11px 13px;
  border-radius: 16px;
  font-size: 0.9rem;
  line-height: 1.45;
  white-space: pre-wrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.help-chat__bubble--bot {
  align-self: flex-start;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-bottom-left-radius: 6px;
}

.help-chat__bubble--user {
  align-self: flex-end;
  background: rgba(var(--v-theme-primary), 0.14);
  color: rgb(var(--v-theme-on-surface));
  border-bottom-right-radius: 6px;
}

.help-chat__typing {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 52px;
  padding-top: 14px;
  padding-bottom: 14px;
}

.help-chat__typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.35);
  animation: help-typing 1.1s ease-in-out infinite;
}

.help-chat__typing-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.help-chat__typing-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes help-typing {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

.help-msg-enter-active {
  transition:
    opacity 0.28s ease,
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.help-msg-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.help-msg-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.help-msg-leave-active {
  transition: opacity 0.16s ease;
  position: absolute;
}

.help-msg-leave-to {
  opacity: 0;
}

.help-msg-move {
  transition: transform 0.28s ease;
}

.help-chat__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.help-chat__option-btn {
  justify-content: flex-start;
  text-transform: none;
  letter-spacing: 0;
}

.help-chat__contact,
.help-chat__composer,
.help-chat__footer-actions {
  padding: 14px 16px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  background: rgba(var(--v-theme-surface), 0.96);
}

.help-chat__composer {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.help-chat__composer-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: end;
}

.help-chat__send {
  margin-bottom: 2px;
}

.help-chat__char-hint {
  font-size: 0.72rem;
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.55);
  padding-left: 2px;
}

.help-chat__char-hint--short {
  color: rgba(var(--v-theme-warning), 1);
}

.help-chat__footer-actions {
  display: flex;
  justify-content: center;
}

.help-chat__footer-actions--stack {
  flex-direction: column;
  gap: 8px;
}

.help-chat-slide-enter-active,
.help-chat-slide-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.help-chat-slide-enter-from,
.help-chat-slide-leave-to {
  opacity: 0;
  transform: translateY(28px) scale(0.96);
}

.help-chat-slide-enter-to,
.help-chat-slide-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (max-width: 600px) {
  .help-chat {
    right: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
  }

  .help-chat__panel {
    height: min(72vh, calc(100vh - 72px));
    border-radius: 20px;
  }
}
</style>
